import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createNoteAction,
  getNotesAction,
} from "../../../../../store/actions/NotesAction";
import {
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";
import "react-toastify/dist/ReactToastify.css";
import { FaMicrophone } from "react-icons/fa";

import {
  handleSuccess,
  showErrorAlert,
} from "../../../../../services/ErrorHandlingService";

import { getRolesWithModulesAction } from "../../../../../store/actions/RoleAction";

import { getNoteCategoriesAction } from "../../../../../store/actions/NoteCategoriesAction";

const AddNotes = ({
  showModal,
  handleClose,
  defaultCategory,
  patientId, 
}) => {
  if (!showModal) return null;

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [noteDescription, setNoteDescription] =
    useState("");

  const [noteCategory, setNoteCategory] =
    useState(null);

  const [errors, setErrors] = useState({});

  const [loading, setLoading] =
    useState(false);

  const [isRecording, setIsRecording] =
    useState(false);

  const [speechLang, setSpeechLang] =
    useState("en-IN");

  // Medical / Non-Medical
  const [medicalType, setMedicalType] =
    useState("Medical");

  // Multi-select roles
  const [selectedRole, setSelectedRole] =
    useState([]);

  const [selectedMedicine, setSelectedMedicine] =
    useState(null);

  const patientDetails = useSelector(
    (state) =>
      state.patients?.patientDetails
  );

  const roles = useSelector(
    (state) => state.role.roles || []
  );

  const medicines = useSelector(
    (state) =>
      state.medicines.medicines
        ?.medicines || []
  );

  const noteCategories = useSelector(
    (state) =>
      state.noteCategory.list || []
  );

  const admin_id = JSON.parse(
    localStorage.getItem("userDetails")
  )?.id;

  const toggleLanguage = () => {
    setSpeechLang((prev) =>
      prev === "en-IN"
        ? "mr-IN"
        : "en-IN"
    );
  };

  useEffect(() => {
    if (
      showModal &&
      noteCategories.length === 0
    ) {
      dispatch(
        getNoteCategoriesAction(false)
      );
    }
  }, [
    showModal,
    dispatch,
    noteCategories.length,
  ]);

  useEffect(() => {
    if (defaultCategory) {
      setNoteCategory({
        value: defaultCategory.id,
        label: defaultCategory.name,
      });
    }
  }, [defaultCategory]);

  useEffect(() => {
    if (roles.length === 0) {
      dispatch(
        getRolesWithModulesAction()
      );
    }
  }, [dispatch, roles.length]);

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert(
        "Your browser does not support Speech Recognition"
      );

      return;
    }

    const recognition =
      new SpeechRecognition();

    recognition.lang = speechLang;

    recognition.continuous = false;

    recognition.interimResults = false;

    setIsRecording(true);

    recognition.onresult = (event) => {
      const spokenText =
        event.results[0][0].transcript;

      setNoteDescription((prev) =>
        prev
          ? prev + " " + spokenText
          : spokenText
      );
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
  };

  // Doctor selected from multi roles
  const isDoctorSelected =
    medicalType === "Non-Medical" &&
    (selectedRole || []).some(
      (role) =>
        role.label
          ?.toLowerCase()
          ?.trim() === "doctor"
    );

  useEffect(() => {
    if (
      !isDoctorSelected &&
      selectedMedicine
    ) {
      setSelectedMedicine(null);
    }
  }, [
    isDoctorSelected,
    selectedMedicine,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);

    const newErrors = {};

    if (!noteDescription.trim()) {
      newErrors.note_description =
        "Note description is required";
    }

    if (!noteCategory) {
      newErrors.note_category =
        "Please select category";
    }

    if (!medicalType) {
      newErrors.note_type =
        "Please select Medical / Non-Medical";
    }

    if (
      medicalType ===
        "Non-Medical" &&
      (!selectedRole ||
        selectedRole.length === 0)
    ) {
      newErrors.role =
        "Please select role for Non-Medical note";
    }

    setErrors(newErrors);

    if (
      Object.keys(newErrors).length > 0
    ) {
      showErrorAlert(
        "Please fill all required fields"
      );

      setLoading(false);

      return;
    }


    if (!patientId) {
      showErrorAlert(
        "No patient selected. Please select a patient before adding notes."
      );

      setLoading(false);

      return;
    }

    const newNote = {
   
      patient_id: patientId,

      note_description:
        noteDescription,

      category_id:
        noteCategory.value,

      note_type: medicalType,

      sub_role: JSON.stringify(
        medicalType ===
          "Non-Medical"
          ? (
              selectedRole || []
            ).map((role) =>
              String(role.value)
            )
          : []
      ),

      medicine_id:
        selectedMedicine?.value ||
        null,

      is_important: "No",

      is_emergency: "No",

      prescription_id: 0,

      followup_id: 0,
    };

    try {
      await dispatch(
        createNoteAction(newNote)
      );

      await dispatch(
        getNotesAction(patientId)
      );

      setNoteDescription("");

      setMedicalType("Medical");

      setSelectedRole([]);

      setSelectedMedicine(null);

      setNoteCategory(null);

      setErrors({});

      handleClose();
    } catch (error) {
      console.error(
        "Form submission failed:",
        error
      );

      showErrorAlert(
        "Failed to add note"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={showModal}
        onHide={handleClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Add Notes
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Note textarea */}
          <div className="position-relative">
            <textarea
              className="form-control pe-5"
              rows="4"
              value={noteDescription}
              onChange={(e) =>
                setNoteDescription(
                  e.target.value
                )
              }
              placeholder="Enter your notes here..."
            />

            {/* Mic */}
            <button
              type="button"
              onClick={startListening}
              style={{
                position: "absolute",
                right: "10px",
                bottom: "10px",
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "none",
                background:
                  isRecording
                    ? "#ff4d4d"
                    : "#f1f3f5",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "center",
                cursor: "pointer",
                transition: "0.3s",
                boxShadow:
                  isRecording
                    ? "0 0 10px 3px rgba(255, 0, 0, 0.6)"
                    : "0 2px 4px rgba(0,0,0,0.12)",
                transform:
                  isRecording
                    ? "scale(1.1)"
                    : "scale(1)",
              }}
            >
              <FaMicrophone
                size={15}
                color={
                  isRecording
                    ? "white"
                    : "#333"
                }
              />
            </button>

            {/* Language */}
            <button
              type="button"
              onClick={toggleLanguage}
              style={{
                position: "absolute",
                right: "55px",
                bottom: "10px",
                padding: "2px 10px",
                borderRadius: "12px",
                border:
                  "1px solid #ccc",
                background: "#fff",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              {speechLang === "en-IN"
                ? "EN"
                : "MR"}
            </button>
          </div>

          {errors.note_description && (
            <p className="text-danger mt-1">
              {
                errors.note_description
              }
            </p>
          )}

          {/* Medicine */}
          {isDoctorSelected && (
            <div className="mt-3">
              <label className="form-label fw-semibold">
                Select Medicine
                (optional):
              </label>

              <Select
                options={medicines.map(
                  (m) => ({
                    value:
                      m.medicine_id,
                    label:
                      m.medicine_name,
                  })
                )}
                value={
                  selectedMedicine
                }
                onChange={(option) =>
                  setSelectedMedicine(
                    option
                  )
                }
                placeholder="Choose medicine"
                isClearable
              />
            </div>
          )}

          {/* Category */}
          <div className="mt-3">
            <label className="form-label">
              Category Type:
            </label>

            <Select
              options={noteCategories.map(
                (cat) => ({
                  value: cat.id,
                  label: cat.name,
                  colour:
                    cat.colour,
                  forground_color:
                    cat.forground_color,
                })
              )}
              value={noteCategory}
              onChange={(option) =>
                setNoteCategory(
                  option
                )
              }
              placeholder="Select Note Category"
              classNamePrefix="select"
            />

            {errors.note_category && (
              <p className="text-danger">
                {
                  errors.note_category
                }
              </p>
            )}
          </div>

          {/* Medical Type */}
          <div className="mt-3">
            <label className="form-label">
              Note Type:
            </label>

            <div className="d-flex gap-3">
              <div>
                <input
                  type="radio"
                  id="medical"
                  name="medicalType"
                  value="Medical"
                  checked={
                    medicalType ===
                    "Medical"
                  }
                  onChange={(e) =>
                    setMedicalType(
                      e.target.value
                    )
                  }
                />

                <label
                  htmlFor="medical"
                  className="ms-2"
                >
                  Medical
                </label>
              </div>

              <div>
                <input
                  type="radio"
                  id="nonMedical"
                  name="medicalType"
                  value="Non-Medical"
                  checked={
                    medicalType ===
                    "Non-Medical"
                  }
                  onChange={(e) =>
                    setMedicalType(
                      e.target.value
                    )
                  }
                />

                <label
                  htmlFor="nonMedical"
                  className="ms-2"
                >
                  Non-Medical
                </label>
              </div>
            </div>
          </div>

          {/* Roles */}
          {medicalType ===
            "Non-Medical" && (
            <div className="mt-3">
              <label className="form-label">
                Select Role:
              </label>

              <Select
                name="role"
                options={roles.map(
                  (role) => ({
                    value:
                      role.role_id,
                    label:
                      role.role_name,
                  })
                )}
                value={
                  selectedRole
                }
                onChange={(opt) =>
                  setSelectedRole(
                    opt || []
                  )
                }
                placeholder="Select Roles"
                classNamePrefix="select"
                isMulti
                closeMenuOnSelect={
                  false
                }
              />

              {errors.role && (
                <p className="text-danger">
                  {errors.role}
                </p>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="primary"
            onClick={
              handleSubmit
            }
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddNotes;